import {
  MailOutlined,
  MessageOutlined,
  BellOutlined,
  ClockCircleOutlined,
  SendOutlined,
} from '@ant-design/icons'
import { useState } from 'react'
import { Modal } from '../Modal'

export type ActivityLogType = 'email' | 'whatsapp' | 'push'

export interface ActivityLogCardProps {
  /**
   * Type of activity log - determines color scheme and icon
   */
  type: ActivityLogType
  /**
   * Title/subject of the message
   */
  title: string
  /**
   * Optional message body/preview
   */
  message?: string
  /**
   * Created/scheduled timestamp
   */
  createdAt?: string
  /**
   * Sent timestamp
   */
  sentAt?: string
  /**
   * Additional CSS classes
   */
  className?: string
}

const CONFIG: Record<
  ActivityLogType,
  {
    accentColor: string
    bgColor: string
    iconColor: string
    icon: React.ReactNode
  }
> = {
  email: {
    accentColor: '#4277BB',
    bgColor: '#DAEDFF',
    iconColor: '#2B65B0',
    icon: <MailOutlined className="text-xl" style={{ color: '#2B65B0' }} />,
  },
  whatsapp: {
    accentColor: '#34A853',
    bgColor: '#D4EDDA',
    iconColor: '#28A745',
    icon: <MessageOutlined className="text-xl" style={{ color: '#28A745' }} />,
  },
  push: {
    accentColor: '#EB1964',
    bgColor: '#FCE4EC',
    iconColor: '#EB1964',
    icon: <BellOutlined className="text-xl" style={{ color: '#EB1964' }} />,
  },
}

/**
 * ActivityLogCard Component
 *
 * Displays communication activity logs for Email Blast, WhatsApp Blast, and Push Notifications.
 *
 * @example
 * <ActivityLogCard
 *   type="email"
 *   title="AAVII Worldwide"
 *   message="Welcome to the AAVII Worldwide Event 2025"
 *   createdAt="2025-07-01, 11:00AM"
 *   sentAt="2025-07-01, 12:30PM"
 * />
 */
export function ActivityLogCard({
  type,
  title,
  message,
  createdAt,
  sentAt,
  className = '',
}: ActivityLogCardProps) {
  const config = CONFIG[type]
  const [isModalOpen, setIsModalOpen] = useState(false)
  const hasMessage = Boolean(message?.trim())

  const openModal = () => {
    if (!hasMessage) return
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  return (
    <>
      <div
        className={`bg-white overflow-hidden rounded-sm border border-gray-200 flex ${
          hasMessage ? 'cursor-pointer hover:border-gray-300' : ''
        } ${className}`}
        role={hasMessage ? 'button' : undefined}
        tabIndex={hasMessage ? 0 : undefined}
        onClick={openModal}
        onKeyDown={(event) => {
          if (!hasMessage) return
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openModal()
          }
        }}
      >
        {/* Accent bar */}
        <div
          className="w-2 self-stretch flex-shrink-0"
          style={{ backgroundColor: config.accentColor }}
        />

        {/* Content */}
        <div className="flex-1 p-2.5">
          <div className="flex items-center gap-2">
            {/* Icon */}
            <div
              className="w-10 h-10 flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: config.bgColor }}
            >
              {config.icon}
            </div>

            {/* Text content */}
            <div className="flex-1 flex flex-col gap-0.5">
              {/* Title and message */}
              <div className="flex flex-col">
                <div className="text-sm font-medium text-black/85">{title}</div>
                {hasMessage && <div className="text-xs text-black/45">View message</div>}
              </div>

              {/* Timestamps */}
              <div className="flex flex-col gap-2">
                {createdAt && (
                  <div className="flex items-center gap-2 text-sm text-black/45 leading-[22px]">
                    <ClockCircleOutlined className="text-sm text-black/85" />
                    <span className="w-[50px]">Created</span>
                    <span>{createdAt}</span>
                  </div>
                )}
                {sentAt && (
                  <div className="flex items-center gap-1 text-xs text-black/45">
                    <SendOutlined className="text-black/85" />
                    <span className="">{sentAt}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title={title}
        open={isModalOpen}
        onClose={closeModal}
        footer={null}
        width={900}
        centered
        destroyOnClose
        zIndex={1100}
      >
        {hasMessage ? (
          <iframe
            srcDoc={message}
            title={`${title} message content`}
            className="w-full h-[65vh] border border-gray-200"
            sandbox="allow-same-origin"
          />
        ) : (
          <div className="text-sm text-black/45">No message content available.</div>
        )}
      </Modal>
    </>
  )
}

export default ActivityLogCard
